/**
 * This module provides a React-Suspense-like framework for the User.
 *
 * It still uses the old `useUser` hook under the hood.
 *
 * - Wrap the component subtree (Possibly near the root) that needs the user with <UserProvider>,
 * with or without a fallback component.
 *
 * - Then, any sub-component can use `useUserContext` to always get a concrete User object.
 *
 * It is named `useUserContext` to avoid confusion with the older `useUser` hook.
 *
 * This module also re-exports `updateUser` and `refreshUser` from `useUser` for convenience.
 */

import type {CoordinateFormat, UnitSystem, User} from '@gaia/types';
import {createContext, useContext, type ReactNode} from 'react';
import useUser from './useUser';

export {refreshUser, updateUser} from './useUser';

const UserContext = createContext<User | undefined>(undefined);

// exported for Jest tests
export const errorMessage = 'useUserContext must be used within a UserProvider';

export function UserProvider({fallback, children}: {fallback?: ReactNode; children: ReactNode}) {
    const [user] = useUser();
    return user ? (
        <UserContext.Provider value={user}>{children}</UserContext.Provider>
    ) : (
        <>{fallback}</>
    );
}

export function useUserContext(): User {
    const user = useContext(UserContext);
    if (user === undefined) throw new Error(errorMessage);
    return user;
}

export function useUserUnitSystem(): UnitSystem {
    return useUserContext().unitSystem || 'imperial';
}

export function useUserCoordinateFormat(): CoordinateFormat {
    return useUserContext().coordinateFormat || 'dd';
}
